import React from 'react';
import {StatusColor} from "./StatusBadge";
import {cn} from "../utils/style";

function InvoiceBanner({status, backendMessage, className, antiFraud}) {
    if (status === 'pending') return null;
    
    const Messages = {
        not_found: "We couldn't find the invoice in our records.",
        unknown_error: "An unexpected error occurred. Please try again later.",
        completed: "Invoice paid successfully!",
        failed: "Payment failed. You can retry the payment.",
        refunded: "Your invoice has been refunded to your original method of payment.",
        canceled: "Your invoice was canceled as requested.",
        expired: "Your invoice has expired due to non-payment. Please consider renewing if you wish to continue with this transaction.",
        on_hold: "Your invoice is currently on hold while we verify the payment information. This may take a few moments."
    }
    
    let message = backendMessage || Messages[status] || '';
    let color = StatusColor[status] || '';
    
    const getStatusConfig = () => {
        switch(status) {
            case 'completed':
            case 'paid':
                return {
                    icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                    ),
                    bgGradient: 'from-green-500 to-green-600',
                    shadowColor: 'shadow-green-200'
                };
            case 'failed':
            case 'expired':
            case 'not_found':
            case 'unknown_error':
                return {
                    icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                    ),
                    bgGradient: 'from-rose-500 to-rose-600',
                    shadowColor: 'shadow-rose-200'
                };
            case 'refunded':
                return {
                    icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                    ),
                    bgGradient: 'from-teal-500 to-teal-600',
                    shadowColor: 'shadow-teal-200'
                };
            case 'canceled':
                return {
                    icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                    ),
                    bgGradient: 'from-rose-400 to-rose-500',
                    shadowColor: 'shadow-rose-200'
                };
            case 'on_hold':
                return {
                    icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    ),
                    bgGradient: 'from-amber-500 to-amber-600',
                    shadowColor: 'shadow-amber-200'
                };
            default:
                return {
                    icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                        </svg>
                    ),
                    bgGradient: 'from-blue-500 to-blue-600',
                    shadowColor: 'shadow-blue-200'
                };
        }
    };

    const statusConfig = getStatusConfig();

    if (antiFraud) {
        return (
            <div className={cn(
                "p-4 rounded-lg shadow-md border-0 text-white animate-fade-in transition-all duration-300",
                "bg-gradient-to-br from-rose-600 to-rose-700",
                "flex items-center gap-3",
                className
            )}>
                <div className="bg-white/20 p-2 rounded-full flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="flex-1">
                    <p className="font-medium">This payment has been flagged due to unusual activity.</p>
                    <p className="text-sm opacity-90 mt-1">If you believe this was done in error, please contact our support team for assistance.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={cn(
            "p-4 rounded-lg shadow-md border-0 text-white animate-fade-in transition-all duration-300",
            `bg-gradient-to-br ${statusConfig.bgGradient}`,
            "flex items-center gap-3",
            className
        )}>
            <div className="bg-white/20 p-2 rounded-full flex-shrink-0">
                {statusConfig.icon}
            </div>
            <div className="flex-1 font-medium">
                {message}
            </div>
        </div>
    );
}

export default InvoiceBanner;